import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  AlertTriangle,
  BarChart3,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Clock3,
  FileText,
  MapPin,
  RefreshCw,
  Search,
  Users,
} from "lucide-react";
import "../../styles/mudur/EgitimDashboard.css";

const apiListesi = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.$values)) return value.$values;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.data?.$values)) return value.data.$values;
  return [];
};

const durumSinifi = (durum = "") => {
  const map = {
    "Tamamlandı": "tamamlandi",
    "Devam Ediyor": "devam-ediyor",
    "Başlamadı": "baslamadi",
    "Süresi Geçti": "suresi-gecti",
    "Atama Yok": "atama-yok",
  };
  return map[durum] || "atama-yok";
};

const tarihYaz = (value) => {
  if (!value) return "-";
  const tarih = new Date(value);
  if (Number.isNaN(tarih.getTime())) return "-";
  return tarih.toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const metinKey = (value = "") =>
  String(value || "").trim().toLocaleLowerCase("tr-TR");

const apiUrlTamamla = (url = "") => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;

  const apiBaseUrl = String(process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
  return `${apiBaseUrl}/${String(url).replace(/^\//, "")}`;
};

const personelKayitlari = (personel) => [
  ...apiListesi(personel?.egitimler),
  ...apiListesi(personel?.anketler),
  ...apiListesi(personel?.sinavlar),
];

const turIkonu = (tur) => {
  if (tur === "Eğitim") return <BookOpen size={18} />;
  if (tur === "Anket") return <ClipboardList size={18} />;
  return <FileText size={18} />;
};

export default function EgitimDashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [arama, setArama] = useState("");
  const [tur, setTur] = useState("Tümü");
  const [durum, setDurum] = useState("Tümü");
  const [acikPersoneller, setAcikPersoneller] = useState(() => new Set());
  const [personelFotoMap, setPersonelFotoMap] = useState({});
  const personelFotoUrlRef = useRef({});

  const dashboardGetir = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/mudur/egitim-dashboard`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setDashboard(response.data?.data || null);
    } catch (err) {
      if (err.response?.status === 403) {
        setError("Bu ekran yalnızca mağaza müdürü rolündeki kullanıcılara açıktır.");
      } else {
        setError(
          err.response?.data?.message ||
            "Eğitim durumu alınamadı. Lütfen tekrar deneyin."
        );
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    dashboardGetir();
  }, [dashboardGetir]);

  const personeller = useMemo(
    () => apiListesi(dashboard?.personeller),
    [dashboard]
  );

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token || personeller.length === 0) return;

    const fotograflariBekleyenPersoneller = personeller.filter(
      (personel) =>
        personel.profilFotoEndpoint &&
        !personelFotoUrlRef.current[personel.kullaniciId]
    );

    if (fotograflariBekleyenPersoneller.length === 0) return;

    let iptalEdildi = false;

    const fotograflariGetir = async () => {
      const yeniFotolar = {};

      await Promise.allSettled(
        fotograflariBekleyenPersoneller.map(async (personel) => {
          const response = await axios.get(
            apiUrlTamamla(personel.profilFotoEndpoint),
            {
              headers: { Authorization: `Bearer ${token}` },
              responseType: "blob",
            }
          );

          const objectUrl = URL.createObjectURL(response.data);

          if (iptalEdildi) {
            URL.revokeObjectURL(objectUrl);
            return;
          }

          personelFotoUrlRef.current[personel.kullaniciId] = objectUrl;
          yeniFotolar[personel.kullaniciId] = objectUrl;
        })
      );

      if (!iptalEdildi && Object.keys(yeniFotolar).length > 0) {
        setPersonelFotoMap((onceki) => ({ ...onceki, ...yeniFotolar }));
      }
    };

    fotograflariGetir();

    return () => {
      iptalEdildi = true;
    };
  }, [personeller]);

  useEffect(() => {
    const fotoUrlMap = personelFotoUrlRef.current;

    return () => {
      Object.values(fotoUrlMap).forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const filtreliPersoneller = useMemo(() => {
    const aramaKey = metinKey(arama);

    return personeller
      .map((personel) => {
        const tumKayitlar = personelKayitlari(personel);
        const personelEslesiyor = [
          personel.adSoyad,
          personel.pozisyon,
          personel.magazaAdi,
        ].some((value) => metinKey(value).includes(aramaKey));

        const gorunenKayitlar = tumKayitlar.filter((kayit) => {
          const turUygun = tur === "Tümü" || kayit.tur === tur;
          const durumUygun = durum === "Tümü" || kayit.durum === durum;
          const metinUygun =
            !aramaKey ||
            personelEslesiyor ||
            metinKey(kayit.baslik).includes(aramaKey);
          return turUygun && durumUygun && metinUygun;
        });

        const kayitsizPersonelGoster =
          tumKayitlar.length === 0 &&
          tur === "Tümü" &&
          durum === "Tümü" &&
          (!aramaKey || personelEslesiyor);

        if (gorunenKayitlar.length === 0 && !kayitsizPersonelGoster) return null;
        return { ...personel, gorunenKayitlar };
      })
      .filter(Boolean);
  }, [arama, durum, personeller, tur]);

  const personelAcKapat = (kullaniciId) => {
    setAcikPersoneller((onceki) => {
      const yeni = new Set(onceki);
      if (yeni.has(kullaniciId)) yeni.delete(kullaniciId);
      else yeni.add(kullaniciId);
      return yeni;
    });
  };

  if (loading) {
    return (
      <div className="egitim-dashboard-loading">
        <RefreshCw className="egitim-dashboard-spin" size={30} />
        <span>Mağaza eğitim durumu hazırlanıyor...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="egitim-dashboard-error">
        <AlertTriangle size={34} />
        <h2>Dashboard açılamadı</h2>
        <p>{error}</p>
        <button type="button" onClick={dashboardGetir}>
          Tekrar dene
        </button>
      </div>
    );
  }

  const ozet = dashboard?.ozet || {};
  const ozetKartlari = [
    {
      label: "Personel",
      value: ozet.personelSayisi || 0,
      icon: <Users size={22} />,
      tone: "lacivert",
    },
    {
      label: "Toplam İçerik",
      value: ozet.toplamAtama || 0,
      icon: <BarChart3 size={22} />,
      tone: "mor",
    },
    {
      label: "Tamamlandı",
      value: ozet.tamamlandi || 0,
      icon: <CheckCircle2 size={22} />,
      tone: "yesil",
    },
    {
      label: "Devam Ediyor",
      value: ozet.devamEdiyor || 0,
      icon: <Clock3 size={22} />,
      tone: "mavi",
    },
    {
      label: "Başlamadı",
      value: ozet.baslamadi || 0,
      icon: <BookOpen size={22} />,
      tone: "gri",
    },
    {
      label: "Süresi Geçti",
      value: ozet.suresiGecti || 0,
      icon: <AlertTriangle size={22} />,
      tone: "kirmizi",
    },
  ];

  return (
    <div className="personel-icerik egitim-dashboard-page">
        <header className="egitim-dashboard-hero">
          <div>
            <span className="egitim-dashboard-eyebrow">MAĞAZA EKİP TAKİBİ</span>
            <h2>Dashboard</h2>
            <p>
              Ekibinizin eğitim, anket ve sınav süreçlerini tek ekrandan takip edin.
            </p>
            <div className="egitim-dashboard-location">
              <MapPin size={17} />
              <span>{dashboard?.magazaAdi || "Mağazam"}</span>
            </div>
          </div>
          <div className="egitim-dashboard-rate">
            <span>Tamamlanma oranı</span>
            <strong>%{Number(ozet.tamamlanmaOrani || 0).toLocaleString("tr-TR")}</strong>
            <div className="egitim-dashboard-rate-track">
              <span
                style={{
                  width: `${Math.min(100, Number(ozet.tamamlanmaOrani || 0))}%`,
                }}
              />
            </div>
          </div>
        </header>

        <section className="egitim-dashboard-summary" aria-label="Durum özeti">
          {ozetKartlari.map((kart) => (
            <article key={kart.label} className={`egitim-summary-card ${kart.tone}`}>
              <div className="egitim-summary-icon">{kart.icon}</div>
              <div>
                <strong>{kart.value}</strong>
                <span>{kart.label}</span>
              </div>
            </article>
          ))}
        </section>

        <section className="egitim-dashboard-content">
          <div className="egitim-dashboard-toolbar">
            <div>
              <h2>Personel durumu</h2>
              <p>{filtreliPersoneller.length} personel listeleniyor</p>
            </div>
            <button
              type="button"
              className="egitim-dashboard-refresh"
              onClick={dashboardGetir}
              title="Verileri yenile"
            >
              <RefreshCw size={18} />
              Yenile
            </button>
          </div>

          <div className="egitim-dashboard-filters">
            <label className="egitim-dashboard-search">
              <Search size={19} />
              <input
                value={arama}
                onChange={(event) => setArama(event.target.value)}
                placeholder="Personel veya atama ara"
              />
            </label>
            <select value={tur} onChange={(event) => setTur(event.target.value)}>
              <option>Tümü</option>
              <option>Eğitim</option>
              <option>Anket</option>
              <option>Sınav</option>
            </select>
            <select value={durum} onChange={(event) => setDurum(event.target.value)}>
              <option>Tümü</option>
              <option>Tamamlandı</option>
              <option>Devam Ediyor</option>
              <option>Başlamadı</option>
              <option>Süresi Geçti</option>
            </select>
          </div>

          <div className="egitim-personel-listesi">
            {filtreliPersoneller.map((personel) => {
              const acik = acikPersoneller.has(personel.kullaniciId);
              const personelFoto = personelFotoMap[personel.kullaniciId];
              const basHarfler = String(personel.adSoyad || "P")
                .split(" ")
                .filter(Boolean)
                .slice(0, 2)
                .map((parca) => parca[0])
                .join("")
                .toLocaleUpperCase("tr-TR");
              return (
                <article className="egitim-personel-card" key={personel.kullaniciId}>
                  <button
                    type="button"
                    className="egitim-personel-head"
                    onClick={() => personelAcKapat(personel.kullaniciId)}
                    aria-expanded={acik}
                  >
                    <div className="egitim-personel-identity">
                      <span className="egitim-personel-avatar">
                        {personelFoto ? (
                          <img src={personelFoto} alt={personel.adSoyad} />
                        ) : (
                          basHarfler
                        )}
                      </span>
                      <span>
                        <strong>{personel.adSoyad}</strong>
                        <small>{personel.pozisyon} · {personel.magazaAdi}</small>
                      </span>
                    </div>
                    <div className="egitim-personel-counts">
                      <span><b>{personel.ozet?.tamamlandi || 0}</b> Tamam</span>
                      <span><b>{personel.ozet?.devamEdiyor || 0}</b> Devam</span>
                      <span><b>{personel.ozet?.baslamadi || 0}</b> Başlamadı</span>
                    </div>
                    <span className={`egitim-status-badge ${durumSinifi(personel.genelDurum)}`}>
                      {personel.genelDurum}
                    </span>
                    <span className="egitim-personel-chevron">
                      {acik ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </span>
                  </button>

                  {acik && (
                    <div className="egitim-personel-details">
                      {personel.gorunenKayitlar.length === 0 ? (
                        <div className="egitim-personel-empty">
                          Bu personele henüz eğitim, anket veya sınav atanmamış.
                        </div>
                      ) : (
                        personel.gorunenKayitlar.map((kayit) => (
                          <div
                            className="egitim-assignment-row"
                            key={`${kayit.tur}-${kayit.kaynak || "platform"}-${kayit.atamaId}`}
                          >
                            <div className={`egitim-assignment-icon ${metinKey(kayit.tur)}`}>
                              {turIkonu(kayit.tur)}
                            </div>
                            <div className="egitim-assignment-main">
                              <span className="egitim-assignment-type">
                                {kayit.tur}{kayit.kaynak ? ` · ${kayit.kaynak}` : ""}
                              </span>
                              <strong>{kayit.baslik}</strong>
                              {kayit.tur === "Eğitim" && kayit.ilerlemeYuzdesi !== null && (
                                <div className="egitim-assignment-progress">
                                  <span><i style={{ width: `${kayit.ilerlemeYuzdesi}%` }} /></span>
                                  <small>%{kayit.ilerlemeYuzdesi}</small>
                                </div>
                              )}
                            </div>
                            <div className="egitim-assignment-dates">
                              <span>
                                {kayit.kaynak === "İSG" ? "Başlangıç" : "Atama"}{" "}
                                <b>{tarihYaz(kayit.atamaTarihi)}</b>
                              </span>
                              {kayit.kaynak !== "İSG" && (
                                <span>Son tarih <b>{tarihYaz(kayit.sonTarih)}</b></span>
                              )}
                              {kayit.sonHareketTarihi && (
                                <span>Son hareket <b>{tarihYaz(kayit.sonHareketTarihi)}</b></span>
                              )}
                            </div>
                            <span className={`egitim-status-badge ${durumSinifi(kayit.durum)}`}>
                              {kayit.durum}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </article>
              );
            })}

            {filtreliPersoneller.length === 0 && (
              <div className="egitim-dashboard-empty">
                <Search size={30} />
                <h3>Sonuç bulunamadı</h3>
                <p>Arama veya filtre seçiminizi değiştirip tekrar deneyin.</p>
              </div>
            )}
          </div>
        </section>
    </div>
  );
}
